import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { usePet } from '../context/PetContext';
import { useTrialStatus } from './useTrialStatus';
import type { PetMember, PetInvite } from '../types';

export interface UsePetMembersReturn {
  members: PetMember[];
  invites: PetInvite[];
  loading: boolean;
  seatLimit: number;
  isHandoffMode: boolean;
  /** True if the current user owns the active pet (not a shared carer). */
  isOwner: boolean;
  /** Creates a new invite and returns the shareable accept URL. */
  inviteCarer: () => Promise<string>;
  /** Transfer active pet ownership to another fully-onboarded subscriber. */
  transferOwnership: (recipientEmail: string) => Promise<void>;
  removeCarer: (uid: string) => Promise<void>;
  cancelInvite: (inviteId: string) => Promise<void>;
  /** Builds an accept URL for any invite token. */
  buildInviteUrl: (token: string, inviteId?: string) => string;
}

export function usePetMembers(): UsePetMembersReturn {
  const { user } = useAuth();
  const { activePetId, activePetOwnerUid } = usePet();
  const { subscription } = useTrialStatus();
  const [members, setMembers] = useState<PetMember[]>([]);
  const [invites, setInvites] = useState<PetInvite[]>([]);
  const [loading, setLoading] = useState(true);

  // The current user owns this pet if they are the owner_uid
  const isOwner = !!user?.id && user.id === activePetOwnerUid;
  const segment = String(
    ((subscription?.raw as { metadata?: Record<string, unknown> } | undefined)?.metadata?.segment as string | undefined) ?? ''
  ).toLowerCase();
  const isHandoffMode = segment === 'pet_sitter';
  const seatLimit = isHandoffMode ? 0 : 2;

  useEffect(() => {
    if (!activePetOwnerUid || !activePetId) {
      setLoading(false);
      return;
    }

    const petBase = `users/${activePetOwnerUid}/pets/${activePetId}`;

    const unsubMembers = onSnapshot(
      collection(db, petBase, 'members'),
      snap => {
        setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() } as PetMember)));
        setLoading(false);
      },
      () => setLoading(false)
    );

    const unsubInvites = onSnapshot(
      collection(db, petBase, 'invites'),
      snap => {
        setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() } as PetInvite)));
      }
    );

    return () => { unsubMembers(); unsubInvites(); };
  }, [activePetOwnerUid, activePetId]);

  const buildInviteUrl = useCallback((token: string, inviteId?: string): string => {
    const base = window.location.origin;
    const params = new URLSearchParams({
      mode: 'acceptPetInvite',
      token,
      petId: activePetId ?? '',
      ownerUid: activePetOwnerUid ?? '',
    });

    if (inviteId) params.set('inviteId', inviteId);

    return `${base}/auth/action?${params.toString()}`;
  }, [activePetId, activePetOwnerUid]);

  const inviteCarer = useCallback(async (): Promise<string> => {
    if (!user?.id || !activePetId || !activePetOwnerUid) throw new Error('Not ready');
    if (!isOwner) throw new Error('Only the owner can invite co-carers');
    if (seatLimit <= 0) throw new Error('This account uses handoff mode and has no co-carer seats.');

    const petsSnap = await getDocs(collection(db, 'users', activePetOwnerUid, 'pets'));
    const uniqueCarerUids = new Set<string>();
    let pendingInvitesTotal = 0;

    for (const petDoc of petsSnap.docs) {
      const [membersSnap, invitesSnap] = await Promise.all([
        getDocs(collection(db, 'users', activePetOwnerUid, 'pets', petDoc.id, 'members')),
        getDocs(collection(db, 'users', activePetOwnerUid, 'pets', petDoc.id, 'invites')),
      ]);

      membersSnap.docs.forEach((memberDoc) => {
        const member = memberDoc.data() as Partial<PetMember>;
        if ((member.role ?? 'carer') === 'carer') uniqueCarerUids.add(memberDoc.id);
      });

      pendingInvitesTotal += invitesSnap.docs.filter((inviteDoc) => {
        const invite = inviteDoc.data() as Partial<PetInvite>;
        return invite.status === 'pending';
      }).length;
    }

    if (uniqueCarerUids.size >= seatLimit || uniqueCarerUids.size + pendingInvitesTotal >= seatLimit) {
      throw new Error(`Co-carer seat limit reached (${seatLimit} total).`);
    }

    const token = crypto.randomUUID();
    const petBase = `users/${activePetOwnerUid}/pets/${activePetId}`;
    const inviteRef = doc(collection(db, petBase, 'invites'));
    await setDoc(inviteRef, {
      pet_id: activePetId,
      owner_uid: activePetOwnerUid,
      invited_by_name: user.full_name || 'Your co-carer',
      status: 'pending',
      created_at: new Date().toISOString(),
      token,
    } satisfies Omit<PetInvite, 'id' | 'email'>);

    return buildInviteUrl(token, inviteRef.id);
  }, [user, activePetId, activePetOwnerUid, isOwner, seatLimit, buildInviteUrl]);

  const transferOwnership = useCallback(async (recipientEmail: string) => {
    if (!activePetId) throw new Error('No active pet selected.');
    if (!isOwner) throw new Error('Only the owner can transfer ownership.');

    const functions = getFunctions();
    const callable = httpsCallable(functions, 'transferPetOwnershipCallable');
    await callable({ petId: activePetId, recipientEmail });
  }, [activePetId, isOwner]);

  const removeCarer = useCallback(async (carerUid: string) => {
    if (!activePetOwnerUid || !activePetId || !isOwner) return;
    const petBase = `users/${activePetOwnerUid}/pets/${activePetId}`;
    // Delete the member doc on the owner side. The carer-side
    // shared_pet_refs/{petId} pointer is cleaned up by the
    // onPetMemberDeleted Cloud Function trigger (the owner has no
    // permission to write under the carer's user tree).
    await deleteDoc(doc(db, petBase, 'members', carerUid));
  }, [activePetOwnerUid, activePetId, isOwner]);

  const cancelInvite = useCallback(async (inviteId: string) => {
    if (!activePetOwnerUid || !activePetId || !isOwner) return;
    const petBase = `users/${activePetOwnerUid}/pets/${activePetId}`;
    await deleteDoc(doc(db, petBase, 'invites', inviteId));
  }, [activePetOwnerUid, activePetId, isOwner]);

  return {
    members,
    invites,
    loading,
    seatLimit,
    isHandoffMode,
    isOwner,
    inviteCarer,
    transferOwnership,
    removeCarer,
    cancelInvite,
    buildInviteUrl,
  };
}
